import React, { useState, useEffect, useContext } from 'react';

import { collection, query, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { Card, CardHeader, CardContent, IconButton, Modal, Typography, Box, Stack, FormHelperText, TextField, List, ListItem, ListItemText } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Container } from '@mui/system';
import { Add, Delete } from '@mui/icons-material';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from "yup";

import { db } from '../../../firebase';
import AdminNavbar from '../../../components/AdminNavbar';
import AlertsContext from '../../../AlertsContext';
import LocationCard from './LocationCard';
import GiftCardsCard from './GiftCardsCard';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const schema = yup.object({
  name: yup.string().required().label('Name'),
  registrationCode: yup.string().required().label('Registration Code'),
}).required();

function Settings() {
  const { setAlert } = useContext(AlertsContext);
  const [readers, setReaders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { control, handleSubmit, reset, formState: { errors, isDirty, isValid } } = useForm({
    defaultValues: {
      name: '',
      registrationCode: '',
    },
    resolver: yupResolver(schema)
  });

  const readerCollection = collection(db, 'readers');

  const onSubmit = async data => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }

    setIsLoading(true);
    try {
      const res = await fetch(process.env.REACT_APP_FN_URL + "/addTerminal", requestOptions);
      const result = await res.json();
      console.log(result);
      reset();
      setOpenModal(false);
      setAlert("Reader is added successful.");
    } catch (error) {
      setAlert({
        type: 'error',
        message: "Error."
      });
    } finally {
      setIsLoading(false);
    }
  }

  const deleteReader = async (id) => {
    const readersRef = doc(db, 'readers', id);
    try {
      setIsLoading(true)
      await deleteDoc(readersRef);
      setAlert("Reader is deleted successful.");
    } catch (e) {
      setAlert({
        type: 'error',
        message: "Error."
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onSnapshot(query(readerCollection), (querySnapshot) => {
      const list = [];
      querySnapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id });
      });
      setReaders(list);
    });
  }, []);

  return (
    <>
      <AdminNavbar />
      <Container component="main" maxWidth="xs" sx={{
        marginTop: '16px',
        marginBottom: '16px',
      }}>
        <h2>Settings</h2>
        <Stack spacing={2}>
          <Card>
            <CardHeader
              title="Terminal"
              action={
                <IconButton aria-label="settings" onClick={() => setOpenModal(true)}>
                  <Add />
                </IconButton>
              }
            >
              Pair Terminal
            </CardHeader>
            <CardContent>
              <Stack spacing={2}>
                <List sx={{ paddingX: 0 }}>
                  {readers.length > 0 ? <>
                    {readers.map((reader) => (
                      <ListItem key={reader.id} sx={{ paddingX: 0 }}>
                        <ListItemText
                          primary={reader.name}
                        />
                        <IconButton onClick={() => deleteReader(reader.id)}>
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                  </> : <p>No readers</p>}
                </List>
              </Stack>
            </CardContent>
          </Card>
          <LocationCard />
          <GiftCardsCard />
        </Stack>
      </Container>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{
            mb: 4,
          }}>
            Add Terminal
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => <TextField type="text" id="outlined-basic" label="Terminal Name" variant="outlined" {...field} />}
              />
              {
                errors?.name && (
                  <FormHelperText id="my-helper-text" error>{errors.name?.message}</FormHelperText>
                )
              }
              <Controller
                name="registrationCode"
                control={control}
                render={({ field }) => <TextField type="text" id="outlined-basic" label="Registration Code" variant="outlined" {...field} />}
              />
              {
                errors?.registrationCode && (
                  <FormHelperText id="my-helper-text" error>{errors.registrationCode?.message}</FormHelperText>
                )
              }
              <LoadingButton
                disabled={!isDirty && !isValid}
                loading={isLoading}
                sx={{ margin: '0 auto', marginTop: 2, alignItems: 'center' }}
                type='submit'
                variant='contained'
              >
                Add
              </LoadingButton>
            </Stack>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default Settings;
